import { IPCodeInputCaretPoint, IPCodeInputHandle } from 'baseComponents';
import {
  computeSuggestion,
  ExpressionError,
  ExpressionReference,
  ExpressionSuggestion,
  ExpressionValidationState,
  validateExpression,
  workedExample,
} from 'components/KPI/Expression/expressionEditor.helpers';
import { FocusEvent, FormEvent, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 400;
const BLUR_CLOSE_MS = 150;
const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];

interface Verdict {
  text: string;
  error?: ExpressionError;
  example?: string;
}

interface ActiveSuggestion extends ExpressionSuggestion {
  active: number;
}

interface UseExpressionSuggestionsParams {
  text: string;
  nameMode: boolean;
  references: ExpressionReference[];
  disabled?: boolean;
  hasSuggestions: boolean;
  applyText: (next: string) => void;
}

export const useExpressionSuggestions = (params: UseExpressionSuggestionsParams) => {
  const { text, nameMode, references, disabled, hasSuggestions, applyText } = params;
  const [suggestion, setSuggestion] = useState<ActiveSuggestion | undefined>(undefined);
  const [caretPoint, setCaretPoint] = useState<IPCodeInputCaretPoint | undefined>(undefined);
  const inputRef = useRef<IPCodeInputHandle>(null);
  const blurTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
    },
    [],
  );

  const focusCaretAt = (position: number) => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(position, position);
    });
  };

  const refreshSuggestion = (next: string, caret: number) => {
    if (disabled || !hasSuggestions) return;
    const result = computeSuggestion(next, caret, references);
    setSuggestion(result ? { ...result, active: 0 } : undefined);
  };

  const spliceToken = (reference: ExpressionReference, from: number, to: number) => {
    const token = nameMode ? `[${reference.name}]` : `[${reference.id}]`;
    const before = text.slice(0, from);
    applyText(`${before}${token}${text.slice(to)}`);
    focusCaretAt(before.length + token.length);
  };

  const insertSuggestion = (reference: ExpressionReference) => {
    if (!suggestion) return;
    spliceToken(reference, suggestion.from, suggestion.to);
    setSuggestion(undefined);
  };

  const insertAtCaret = (reference: ExpressionReference) => {
    const caret = inputRef.current?.getSelectionStart() ?? text.length;
    spliceToken(reference, caret, caret);
    setSuggestion(undefined);
  };

  const applySuggestionFix = (badName: string, suggestedName: string) => {
    const suggestedReference = references.find(
      (reference) => reference.name.trim().toLowerCase() === suggestedName.trim().toLowerCase(),
    );
    if (!suggestedReference) return;

    const re = /\[([^\]]*)\]/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      if (match[1].trim().toLowerCase() === badName.trim().toLowerCase()) {
        const before = text.slice(0, match.index);
        const token = nameMode ? `[${suggestedReference.name}]` : `[${suggestedReference.id}]`;
        applyText(`${before}${token}${text.slice(match.index + match[0].length)}`);
        setSuggestion(undefined);
        focusCaretAt(before.length + token.length);

        return;
      }
    }
  };

  const onFieldChange = (_event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    const next = newValue ?? '';
    applyText(next);
    refreshSuggestion(next, inputRef.current?.getSelectionStart() ?? next.length);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!suggestion) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSuggestion({ ...suggestion, active: (suggestion.active + 1) % suggestion.items.length });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSuggestion({
        ...suggestion,
        active: (suggestion.active - 1 + suggestion.items.length) % suggestion.items.length,
      });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      insertSuggestion(suggestion.items[suggestion.active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setSuggestion(undefined);
    }
  };

  const onKeyUp = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (NAV_KEYS.includes(event.key)) return;
    const caret = inputRef.current?.getSelectionStart();
    if (caret !== null && caret !== undefined) refreshSuggestion(text, caret);
  };

  const onClick = (_event: MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const caret = inputRef.current?.getSelectionStart();
    if (caret !== null && caret !== undefined) refreshSuggestion(text, caret);
  };

  const onBlur = (_event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    blurTimer.current = window.setTimeout(() => setSuggestion(undefined), BLUR_CLOSE_MS);
  };

  return {
    suggestion,
    setSuggestion,
    caretPoint,
    setCaretPoint,
    inputRef,
    applySuggestionFix,
    insertSuggestion,
    insertAtCaret,
    onFieldChange,
    onKeyDown,
    onKeyUp,
    onClick,
    onBlur,
  };
};

interface UseExpressionValidationParams {
  text: string;
  references: ExpressionReference[];
  poolProvided: boolean;
  grandfatheredIds: Set<number>;
  onValidationStateChange?: (state: ExpressionValidationState) => void;
}

export const useExpressionValidation = (params: UseExpressionValidationParams) => {
  const { text, references, poolProvided, grandfatheredIds, onValidationStateChange } = params;
  const [verdict, setVerdict] = useState<Verdict | undefined>(undefined);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const error = text.trim()
        ? validateExpression(text, { references: poolProvided ? references : undefined, grandfatheredIds })
        : undefined;
      const valid = !!text.trim() && !error;
      setVerdict({ text, error, example: valid ? workedExample(text) : undefined });
      onValidationStateChange?.(valid ? 'valid' : 'invalid');
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [text, references, poolProvided, grandfatheredIds, onValidationStateChange]);

  const currentVerdict = verdict?.text === text ? verdict : undefined;

  // Reporting a fresh edit: drop the stale verdict and flag re-validation as pending.
  const reset = () => {
    setVerdict(undefined);
    onValidationStateChange?.('pending');
  };

  return { currentVerdict, error: currentVerdict?.error, reset };
};
